import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { ConfidenceMeter } from '@/components/ui/ConfidenceMeter'
import { EnsembleLoader } from '@/components/ui/LoadingState'
import { ComplianceCard } from '@/components/ui/ComplianceCard'
import { GridRipple } from '@/components/brand/EchoArc'
import { formatPercent, formatWilson, cn } from '@/lib/utils'
import { runDemoEnsemble } from '@/lib/ensemble/demoRunner'
import { DEMO_SEGMENTS, DEMO_STIMULI, DEMO_PRE_READ_QUESTION } from '@/lib/demo/data'
import type { SegmentAggregate, ValidationCell } from '@/lib/types'
import { buildValidationPlan } from '@/lib/ensemble/validation'
import { runIndustryComplianceCheck } from '@/lib/ensemble/passes/IndustryComplianceCheckPass'
import { INDUSTRY_LABELS, ALL_INDUSTRIES } from '@/lib/industry-packs/registry'
import type { ComplianceCheckResult } from '@/lib/ensemble/passes/IndustryComplianceCheckPass'

type Phase = 'compose' | 'running' | 'results' | 'error'

type Stimulus = { label: string; content: string }

export function PreReadPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const isDemo = !projectId || projectId === 'demo'

  const [phase, setPhase] = useState<Phase>('compose')
  const [loaderStep, setLoaderStep] = useState(0)
  const [question, setQuestion] = useState(isDemo ? DEMO_PRE_READ_QUESTION : '')
  const [businessContext, setBusinessContext] = useState(isDemo ? 'Renewal season messaging for auto-renewal policyholders, Q1 2025.' : '')
  const [industry, setIndustry] = useState('')
  const [aggregates, setAggregates] = useState<SegmentAggregate[]>([])
  const [validationPlan, setValidationPlan] = useState<ValidationCell[]>([])
  const [complianceResult, setComplianceResult] = useState<ComplianceCheckResult | null>(null)
  const [expandedDrivers, setExpandedDrivers] = useState<string | null>(null)
  const [runError, setRunError] = useState<string | null>(null)
  const [preReadId, setPreReadId] = useState<string | null>(null)
  const [simulated, setSimulated] = useState(false)

  // Demo: pre-select all demo segments
  const [selectedSegments, setSelectedSegments] = useState(
    isDemo ? DEMO_SEGMENTS.map(s => s.segment_id) : ['general']
  )

  // Real project: editable stimuli
  const [stimuli, setStimuli] = useState<Stimulus[]>(
    isDemo
      ? DEMO_STIMULI.map(s => ({ label: s.label, content: s.content }))
      : [{ label: 'Option A', content: '' }, { label: 'Option B', content: '' }]
  )

  // Fetch project name for breadcrumb
  const [projectName, setProjectName] = useState('')
  useEffect(() => {
    if (isDemo) return
    const token = localStorage.getItem('echo_session')
    if (!token) return
    fetch(`/api/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() as Promise<{ name: string }> : null)
      .then(p => { if (p?.name) setProjectName(p.name) })
      .catch(() => {})
  }, [projectId, isDemo])

  const addStimulus = () => {
    const next = String.fromCharCode(65 + stimuli.length)
    setStimuli(prev => [...prev, { label: `Option ${next}`, content: '' }])
  }
  const removeStimulus = (i: number) => setStimuli(prev => prev.filter((_, idx) => idx !== i))
  const updateStimulus = (i: number, field: 'label' | 'content', value: string) => {
    setStimuli(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s))
  }

  const runPreRead = async () => {
    setPhase('running')
    setRunError(null)
    setLoaderStep(0)

    if (isDemo) {
      // Client-side demo ensemble
      for (let i = 1; i <= 10; i++) {
        await new Promise(r => setTimeout(r, 600))
        setLoaderStep(i)
      }
      const result = await runDemoEnsemble(question, selectedSegments)
      setAggregates(result.aggregates)
      setValidationPlan(result.validation_plan)

      if (industry) {
        const stimulusText = DEMO_STIMULI.map(s => s.content).join('\n\n')
        const compliance = runIndustryComplianceCheck({ stimulusText, industry })
        setComplianceResult(compliance)
      }
      setSimulated(true)
      setPhase('results')
      return
    }

    // Real project: call API
    const token = localStorage.getItem('echo_session')
    if (!token) {
      setRunError('Not authenticated. Please sign in.')
      setPhase('error')
      return
    }

    const validStimuli = stimuli.filter(s => s.label.trim() && s.content.trim())
    if (validStimuli.length < 2) {
      setRunError('Add at least 2 stimuli with labels and content.')
      setPhase('error')
      return
    }

    // Advance loader while waiting
    const loaderInterval = setInterval(() => {
      setLoaderStep(prev => Math.min(prev + 1, 9))
    }, 1500)

    try {
      const res = await fetch(`/api/projects/${projectId}/pre-read`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          research_question: question,
          business_context: businessContext || undefined,
          stimuli: validStimuli.map((s, i) => ({
            stimulus_id: `s${i + 1}`,
            label: s.label.trim(),
            content: s.content.trim(),
          })),
          segment_ids: selectedSegments,
        }),
      })

      clearInterval(loaderInterval)
      setLoaderStep(10)

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: `HTTP ${res.status}` })) as { error?: string }
        throw new Error(errData.error ?? `HTTP ${res.status}`)
      }

      const data = await res.json() as {
        pre_read_id: string
        status: string
        simulated_only: boolean
        result: SegmentAggregate[]
      }

      setPreReadId(data.pre_read_id)
      setSimulated(data.simulated_only)
      setAggregates(data.result ?? [])
      setValidationPlan(buildValidationPlan(data.result ?? []))

      if (industry && validStimuli.length > 0) {
        const stimText = validStimuli.map(s => s.content).join('\n\n')
        setComplianceResult(runIndustryComplianceCheck({ stimulusText: stimText, industry }))
      }

      setPhase('results')
    } catch (err) {
      clearInterval(loaderInterval)
      setRunError(err instanceof Error ? err.message : 'Pre-read failed')
      setPhase('error')
    }
  }

  const activeStimuli = isDemo ? DEMO_STIMULI : stimuli.filter(s => s.label && s.content).map((s, i) => ({ stimulus_id: `s${i+1}`, ...s }))

  if (phase === 'running') {
    return (
      <div className="p-6 max-w-xl mx-auto pb-20 md:pb-6">
        <h1 className="text-2xl font-medium text-deep-ink mb-2">Running ensemble pre-read…</h1>
        <p className="text-sm text-slate-500 mb-6 flex items-center gap-1.5">
          {isDemo && <Badge variant="demo" size="sm">Demo data</Badge>}
          Running 7 passes{isDemo ? ` across ${selectedSegments.length} segments` : ''}
        </p>
        <Card>
          <EnsembleLoader step={loaderStep} total={11} />
        </Card>
      </div>
    )
  }

  if (phase === 'error') {
    return (
      <div className="p-6 max-w-xl mx-auto pb-20 md:pb-6">
        <h1 className="text-2xl font-medium text-deep-ink mb-4">Pre-read failed</h1>
        <Card className="border-risk-rose/30 bg-red-50/30">
          <p className="text-sm text-risk-rose">{runError}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => setPhase('compose')}>← Back to compose</Button>
        </Card>
      </div>
    )
  }

  if (phase === 'results' && aggregates.length > 0) {
    const bySegment: Record<string, SegmentAggregate[]> = {}
    for (const agg of aggregates) {
      if (!bySegment[agg.segment_id]) bySegment[agg.segment_id] = []
      bySegment[agg.segment_id].push(agg)
    }

    const winnerCounts: Record<string, number> = {}
    aggregates.forEach(a => { winnerCounts[a.predicted_winner] = (winnerCounts[a.predicted_winner] ?? 0) + 1 })
    const overallWinner = Object.entries(winnerCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? ''
    const overallConsensus = (winnerCounts[overallWinner] ?? 0) / aggregates.length

    const projectDisplay = isDemo ? 'Insurance Renewal Message Test' : (projectName || projectId?.slice(0, 12) + '…')
    const validationPath = `/app/projects/${isDemo ? 'demo' : projectId}/validation`

    return (
      <div className="p-6 space-y-6 max-w-5xl pb-20 md:pb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Ensemble Pre-Read</h1>
            <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
              {simulated && <Badge variant="demo" size="sm">Simulated</Badge>}
              {!simulated && <Badge variant="success" size="sm">Live LLM</Badge>}
              {projectDisplay}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setPhase('compose')}>Edit question</Button>
            <Link to={validationPath}>
              <Button variant="primary" size="sm">Plan validation →</Button>
            </Link>
          </div>
        </div>

        <Card className="bg-off-white border-0">
          <p className="text-xs text-slate-400 mb-1">Research question</p>
          <p className="text-sm font-medium text-deep-ink">"{question}"</p>
          {businessContext && <p className="text-xs text-slate-500 mt-1">{businessContext}</p>}
        </Card>

        <Card className="bg-slate-navy text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <GridRipple active className="shrink-0" />
              <div>
                <p className="text-xs text-slate-400 mb-0.5">Ensemble consensus</p>
                <p className="text-base font-medium">
                  {Math.round(overallConsensus * 7)} of 7 passes agree <span className="text-signal-cyan">{overallWinner}</span> wins
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-400">Predicted winner</p>
              <p className="text-lg font-medium text-signal-cyan">{overallWinner}</p>
            </div>
          </div>
        </Card>

        {complianceResult && (
          <ComplianceCard
            industry={complianceResult.industry}
            industryLabel={INDUSTRY_LABELS[complianceResult.industry] ?? complianceResult.industry}
            findings={complianceResult.findings}
            maxSeverity={complianceResult.max_severity}
            plainLanguageScore={complianceResult.plain_language_score}
          />
        )}

        <div className="space-y-4">
          <h2 className="text-base font-medium text-deep-ink">Results by segment</h2>
          {Object.entries(bySegment).map(([segId, segs]) => {
            const segName = segs[0].segment_name
            const conf = segs[0].confidence
            const isLowConf = conf.band === 'Low'
            const distribution = segs[0].predicted_distribution
            const dissentNotes = segs[0].productive_diversity_notes ?? []

            return (
              <Card key={segId} className={cn(isLowConf && 'border-uncertainty-amber/40')}>
                <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-deep-ink">{segName}</p>
                      {isLowConf && <Badge variant="warning" size="sm">Validate required</Badge>}
                    </div>
                    <p className="text-xs text-slate-500">
                      Pass agreement: <span className="font-medium text-deep-ink">{formatPercent(segs[0].ensemble_consensus)}</span>
                      {' · '}Predicted winner: <span className="font-medium text-human-teal">{segs[0].predicted_winner}</span>
                    </p>
                  </div>
                  <div className="text-xs text-slate-400">Wilson: {formatWilson(conf.wilson)}</div>
                </div>

                {/* Distribution bar — uses activeStimuli so it works for both demo and real */}
                <div className="mb-4">
                  <p className="text-xs text-slate-400 mb-2">Predicted preference distribution</p>
                  <div className="flex h-8 rounded-lg overflow-hidden gap-0.5">
                    {activeStimuli.map((stim, i) => {
                      const share = distribution[stim.label] ?? 0
                      const colors = ['bg-slate-200', 'bg-human-teal', 'bg-signal-cyan/60', 'bg-evidence-blue/60']
                      return (
                        <div
                          key={stim.label}
                          className={cn('flex items-center justify-center text-xs transition-all', colors[i % colors.length])}
                          style={{ width: `${share * 100}%` }}
                          title={`${stim.label}: ${formatPercent(share)}`}
                        >
                          {share > 0.12 && (
                            <span className={cn('font-medium', i === 1 ? 'text-white' : 'text-slate-600')}>
                              {formatPercent(share)}
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  <div className="flex flex-wrap gap-4 mt-1">
                    {activeStimuli.map((stim, i) => {
                      const dotColors = ['bg-slate-200', 'bg-human-teal', 'bg-signal-cyan/60', 'bg-evidence-blue/60']
                      return (
                        <div key={stim.label} className="flex items-center gap-1 text-xs text-slate-500">
                          <span className={cn('w-2.5 h-2.5 rounded-sm', dotColors[i % dotColors.length])} />
                          {stim.label}
                        </div>
                      )
                    })}
                  </div>
                </div>

                <div className="mb-4">
                  <button
                    className="text-xs text-human-teal hover:underline mb-2 block"
                    onClick={() => setExpandedDrivers(expandedDrivers === segId ? null : segId)}
                  >
                    {expandedDrivers === segId ? 'Hide' : 'Show'} confidence drivers
                  </button>
                  <ConfidenceMeter
                    confidence={conf}
                    groundTruthPresent={false}
                    showDrivers={expandedDrivers === segId}
                  />
                </div>

                {(segs[0].common_signals ?? []).length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-slate-500 mb-1">Common signals (all passes agree)</p>
                    {(segs[0].common_signals ?? []).map((s, i) => (
                      <p key={i} className="text-xs text-slate-600 flex items-start gap-1.5">
                        <span className="text-validation-green shrink-0">✓</span>{s}
                      </p>
                    ))}
                  </div>
                )}

                {dissentNotes.length > 0 && (
                  <div className="mb-3 bg-amber-50 rounded-lg p-3 border border-uncertainty-amber/30">
                    <p className="text-xs font-medium text-uncertainty-amber mb-1">Dissenting signals</p>
                    {dissentNotes.map((note, i) => (
                      <p key={i} className="text-xs text-slate-600 flex items-start gap-1.5">
                        <span className="text-uncertainty-amber shrink-0 mt-0.5">≠</span>{note}
                      </p>
                    ))}
                  </div>
                )}

                {(segs[0].citations ?? []).length > 0 && (
                  <div className="pt-3 border-t border-border-gray">
                    <p className="text-xs font-medium text-slate-500 mb-2">Evidence citations</p>
                    <div className="space-y-2">
                      {(segs[0].citations ?? []).slice(0, 2).map((cit, i) => (
                        <div key={i} className="text-xs bg-soft-panel rounded-lg p-2.5">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant={cit.evidence_strength === 'High' ? 'success' : 'warning'} size="sm">{cit.evidence_strength}</Badge>
                            <span className="text-slate-400">{cit.source_file} · {cit.segment}</span>
                          </div>
                          <Link to={`/app/projects/${isDemo ? 'demo' : projectId}/data?row=${cit.row_index}`} className="italic text-slate-600 hover:text-human-teal">
                            "{cit.quote.slice(0, 140)}{cit.quote.length > 140 ? '…' : ''}"
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )
          })}
        </div>

        {validationPlan.length > 0 && (
          <Card className="border-human-teal/30 bg-human-teal/5">
            <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
              <h2 className="text-base font-medium text-deep-ink">Human Validation Plan</h2>
              <Link to={validationPath}>
                <Button variant="primary" size="sm">Run validation →</Button>
              </Link>
            </div>
            <div className="space-y-3">
              {validationPlan.map((cell, i) => (
                <div key={i} className="bg-white rounded-lg p-3 border border-border-gray">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-medium text-deep-ink">{cell.segment_name} · {cell.stimulus_label}</p>
                    <Badge variant="warning" size="sm">{cell.sample_size_needed} respondents needed</Badge>
                  </div>
                  <p className="text-xs text-slate-500">{cell.reason}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Echo prediction: <span className="font-medium text-deep-ink">{cell.echo_prediction}</span>
                    {' · '}Wilson: {formatWilson(cell.echo_confidence.wilson)}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        )}

        <Card>
          <h3 className="text-sm font-medium text-deep-ink mb-2">Validation need signal</h3>
          <p className="text-xs text-slate-500 mb-3">Higher diversity means more disagreement between passes — more validation recommended.</p>
          {Object.entries(bySegment).map(([segId, segs]) => (
            <div key={segId} className="flex items-center gap-3 mb-2">
              <span className="text-xs text-slate-500 w-40 truncate">{segs[0].segment_name}</span>
              <div className="flex-1 h-2 bg-soft-panel rounded-full overflow-hidden">
                <div className="h-full bg-uncertainty-amber rounded-full" style={{ width: `${segs[0].prediction_diversity * 100}%` }} />
              </div>
              <span className="text-xs text-slate-400 w-10 text-right">{formatPercent(segs[0].prediction_diversity)}</span>
            </div>
          ))}
        </Card>

        <Card className="bg-soft-panel border-0">
          <p className="text-xs text-slate-500">
            <strong className="text-deep-ink">Method:</strong> 7 ensemble passes (Evidence Grounded · Segment Response · Skeptical Researcher · Behavioral Driver · Counterfactual Risk · Quantitative Distribution · Validation Planner). Confidence reflects evidence coverage and pass agreement — not accuracy. Wilson intervals at 95%.
            {preReadId && <span className="text-slate-400 ml-1">Pre-read ID: {preReadId}</span>}
          </p>
        </Card>
      </div>
    )
  }

  // ── Compose screen ──────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6 max-w-2xl pb-20 md:pb-6">
      <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>New research question</h1>
      <p className="text-sm text-slate-500">The ensemble will run 7 passes and produce a confidence-rated pre-read.</p>
      {isDemo && <Badge variant="demo" size="sm">Demo data — simulated results</Badge>}

      <Card className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-deep-ink mb-1.5">Research question</label>
          <textarea
            className="w-full border border-border-gray rounded-lg px-3 py-2 text-sm text-deep-ink focus:outline-none focus:ring-2 focus:ring-human-teal/30 resize-none"
            rows={2}
            placeholder={isDemo ? '' : 'e.g. Which message version drives higher renewal intent?'}
            value={question}
            onChange={e => setQuestion(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-deep-ink mb-1.5">Business context <span className="text-slate-400 font-normal">(optional)</span></label>
          <textarea
            className="w-full border border-border-gray rounded-lg px-3 py-2 text-sm text-deep-ink focus:outline-none focus:ring-2 focus:ring-human-teal/30 resize-none"
            rows={2}
            placeholder={isDemo ? '' : 'e.g. Q3 product launch targeting SMB segment in APAC'}
            value={businessContext}
            onChange={e => setBusinessContext(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-deep-ink mb-1.5">
            Industry <span className="text-slate-400 font-normal">(for compliance check)</span>
          </label>
          <select
            className="w-full border border-border-gray rounded-lg px-3 py-2 text-sm text-deep-ink focus:outline-none focus:ring-2 focus:ring-human-teal/30 bg-white"
            value={industry}
            onChange={e => setIndustry(e.target.value)}
          >
            <option value="">— skip compliance check —</option>
            {ALL_INDUSTRIES.map(ind => (
              <option key={ind} value={ind}>{INDUSTRY_LABELS[ind]}</option>
            ))}
          </select>
        </div>

        {/* Stimuli — demo shows read-only, real shows editable */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-deep-ink">Stimuli</label>
            {!isDemo && (
              <button className="text-xs text-human-teal hover:underline" onClick={addStimulus} disabled={stimuli.length >= 6}>
                + Add stimulus
              </button>
            )}
          </div>

          {isDemo ? (
            <div className="space-y-2">
              {DEMO_STIMULI.map(stim => (
                <div key={stim.stimulus_id} className="bg-soft-panel rounded-lg px-3 py-2">
                  <p className="text-xs font-medium text-human-teal">{stim.label}</p>
                  <p className="text-xs text-slate-600 mt-0.5">{stim.content}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {stimuli.map((stim, i) => (
                <div key={i} className="bg-soft-panel rounded-lg p-2.5 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <input
                      className="w-28 border border-border-gray rounded px-2 py-1 text-xs text-deep-ink bg-white focus:outline-none focus:ring-1 focus:ring-human-teal/30"
                      placeholder="Label"
                      value={stim.label}
                      onChange={e => updateStimulus(i, 'label', e.target.value)}
                    />
                    {stimuli.length > 2 && (
                      <button className="text-xs text-slate-400 hover:text-risk-rose ml-auto" onClick={() => removeStimulus(i)}>Remove</button>
                    )}
                  </div>
                  <textarea
                    className="w-full border border-border-gray rounded px-2 py-1.5 text-xs text-deep-ink bg-white focus:outline-none focus:ring-1 focus:ring-human-teal/30 resize-none"
                    rows={2}
                    placeholder="Paste the stimulus content (message, copy, concept description…)"
                    value={stim.content}
                    onChange={e => updateStimulus(i, 'content', e.target.value)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Segments — demo shows toggles, real shows single "All respondents" */}
        {isDemo && (
          <div>
            <label className="block text-sm font-medium text-deep-ink mb-2">Audience segments</label>
            <div className="flex flex-wrap gap-2">
              {DEMO_SEGMENTS.map(seg => (
                <button
                  key={seg.segment_id}
                  className={cn(
                    'text-sm px-3 py-1.5 rounded-lg border transition-colors',
                    selectedSegments.includes(seg.segment_id)
                      ? 'bg-human-teal/10 border-human-teal text-human-teal'
                      : 'border-border-gray text-slate-500 hover:border-slate-300'
                  )}
                  onClick={() => setSelectedSegments(prev =>
                    prev.includes(seg.segment_id)
                      ? prev.filter(id => id !== seg.segment_id)
                      : [...prev, seg.segment_id]
                  )}
                >
                  {seg.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {!isDemo && (
          <div>
            <label className="block text-sm font-medium text-deep-ink mb-1">Audience</label>
            <div className="flex items-center gap-2 px-3 py-2 bg-soft-panel rounded-lg">
              <span className="w-2 h-2 rounded-full bg-validation-green shrink-0" />
              <span className="text-sm text-slate-600">All respondents (general population)</span>
            </div>
            <p className="text-xs text-slate-400 mt-1">Segment-level breakdowns are available after defining demographic columns in your data.</p>
          </div>
        )}

        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={runPreRead}
          disabled={!question || (isDemo ? selectedSegments.length === 0 : stimuli.filter(s => s.label && s.content).length < 2)}
        >
          Run Ensemble Pre-Read →
        </Button>
      </Card>
    </div>
  )
}
